import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/twUtils';

const buttonVariants = cva(
  // Base classes
  'inline-flex items-center justify-center whitespace-nowrap rounded-[var(--radius-md)] font-semibold not-supports-[-webkit-touch-callout:none]:transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 shadow-md',
  {
    variants: {
      variant: {
        default: 'bg-primary text-white hover:bg-primary-400',
        'accent-default': 'bg-accent text-white hover:bg-accent-600',
        destructive:
          'bg-[var(--color-destructive)] text-[var(--color-destructive-foreground)] hover:bg-[var(--color-destructive-hover)]',
        outline:
          'border border-[var(--color-border)] bg-white hover:bg-[var(--color-primary-50)] text-primary',
        'accent-outline':
          'border border-[var(--color-border)] bg-white hover:bg-[var(--color-accent-50)] text-accent',
        secondary:
          'bg-[var(--color-primary-100)] text-[var(--color-secondary-foreground)] hover:bg-[var(--color-secondary-hover)]',
        ghost:
          'hover:bg-[var(--color-primary-50)] hover:text-[var(--color-accent-foreground)] shadow-none',
        link: 'text-[var(--color-primary)] underline-offset-4 hover:underline shadow-none',
      },
      size: {
        default: 'h-10 rounded-[var(--radius-md)] px-4 py-2 text-base',
        sm: 'h-9 rounded-[var(--radius-sm)] px-3 text-sm',
        lg: 'h-11 rounded-[var(--radius-lg)] px-8 text-base',
        xl: 'h-12 rounded-[var(--radius-lg)] px-8 text-lg',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
